import React, { useState } from 'react';
import {
	Box,
	IconButton,
	Paper,
	Tab,
	Tabs,
	Tooltip,
	Typography,
} from '@mui/material';
import { ArrowBackIcon } from 'components/icons';
import dayjs from 'dayjs';

import CANparkAdminTable from 'containers/pages/CANpark/components/ticketsTable/TicketsTable';
import TicketsTableFilterBar, {
	TicketFilter,
} from 'containers/pages/CANpark/components/ticketsTable/TicketsTableFilterBar';
import TicketsGrid from 'containers/pages/CANpark/components/ticketsGrid/TicketsGrid';
import CustomPieChart from 'containers/pages/CANparkAdmin/components/charts/CustomPieChart';
import CustomStackedBarChart from 'containers/pages/CANparkAdmin/components/charts/CustomStackedBarChart';
import CustomStackedAreaChart from 'containers/pages/CANparkAdmin/components/charts/CustomStackedAreaChart';
import { Ticket } from 'containers/pages/CANpark/types/ticket';
import TicketModal from 'containers/pages/CANpark/components/TicketModal';
import { ParkingLocation } from 'containers/pages/CANpark/types/parkingLocation';
import { useLotHistoryInfinite } from 'containers/pages/CANpark/hooks/useLotHistoryInfinite';

const getInitialFilter = (): TicketFilter => {
	const now = dayjs();
	return {
		ticketNumber: '',
		licensePlate: '',
		showLog: true,
		showLogAndWarn: true,
		showTow: true,
		startDate: now.startOf('day').toISOString(),
		endDate: now.endOf('day').toISOString(),
		items_per_page: 1000,
		page: 1,
	};
};

const ParkingLocationAdminView: React.FC<{
	activeParkingLocation: ParkingLocation;
	onBack: () => void;
}> = ({ activeParkingLocation, onBack }) => {
	const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
	const [tab, setTab] = useState<'grid' | 'table' | 'charts'>('grid');
	const [filter, setFilter] = useState<TicketFilter>(getInitialFilter);

	const { tickets, isLoadingInitial, isLoadingMore, hasMore, loadMore } =
		useLotHistoryInfinite(activeParkingLocation.id, filter);
	const isOnlyTodayFilter =
		filter.ticketNumber === '' &&
		filter.licensePlate === '' &&
		filter.showLog &&
		filter.showLogAndWarn &&
		filter.showTow &&
		!!filter.startDate &&
		!!filter.endDate &&
		dayjs(filter.startDate).isSame(dayjs().startOf('day')) &&
		dayjs(filter.endDate).isSame(dayjs().endOf('day'));
	const emptyMessage = isOnlyTodayFilter
		? 'No logs yet today.'
		: 'No records found.';

	return (
		<Box sx={{ width: '100%', p: 2 }}>
			<Paper sx={{ p: 2, mb: 2 }} data-tour="monitor-header">
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Tooltip title="Back to parking locations">
						<IconButton onClick={onBack} size="small">
							<ArrowBackIcon />
						</IconButton>
					</Tooltip>
					<Box>
						<Typography variant="h5" fontWeight={400}>
							{activeParkingLocation.name}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Lot history & stats
						</Typography>
					</Box>
				</Box>
			</Paper>

			<Paper sx={{ p: 2 }}>
				<Box data-tour="monitor-filter">
					<TicketsTableFilterBar
						filter={filter}
						setFilter={setFilter}
						parkingLocation={activeParkingLocation}
					/>
				</Box>
				<Tabs
					value={tab}
					onChange={(_, v) => setTab(v)}
					data-tour="monitor-tabs"
					sx={{
						borderBottom: 1,
						borderColor: 'divider',
						mb: 2,
					}}
				>
					<Tab value="grid" label="Grid" />
					<Tab value="table" label="Table" />
					<Tab value="charts" label="Charts" />
				</Tabs>

				{tab === 'grid' && (
					<TicketsGrid
						tickets={tickets}
						isLoadingInitial={isLoadingInitial}
						isLoadingMore={isLoadingMore}
						hasMore={hasMore}
						loadMore={loadMore}
						onTicketClick={(t) => setSelectedTicket(t)}
						parkingLocation={activeParkingLocation}
						emptyMessage={emptyMessage}
					/>
				)}

				{tab === 'table' && (
					<CANparkAdminTable
						tickets={tickets}
						isLoadingInitial={isLoadingInitial}
						isLoadingMore={isLoadingMore}
						hasMore={hasMore}
						loadMore={loadMore}
						onRowClick={(rowParam) =>
							setSelectedTicket(rowParam.row as Ticket)
						}
						isAdmin={true}
						minHeight="650px"
						emptyMessage={emptyMessage}
					/>
				)}

				{tab === 'charts' && (
					<Box
						sx={{
							display: 'flex',
							gap: 2,
							width: '100%',
							justifyContent: 'center',
							flexWrap: 'wrap',
						}}
					>
						<CustomStackedBarChart
							parkingLocation={activeParkingLocation}
						/>
						<CustomStackedAreaChart
							parkingLocation={activeParkingLocation}
						/>
						<CustomPieChart parkingLocation={activeParkingLocation} />
					</Box>
				)}
			</Paper>

			<TicketModal
				open={!!selectedTicket}
				onClose={() => setSelectedTicket(null)}
				selectedTicket={selectedTicket}
				parkingLocation={activeParkingLocation}
			/>
		</Box>
	);
};

export default ParkingLocationAdminView;
